import { Component, forwardRef, inject, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TippyInstance, TippyModule } from '@ngneat/helipopper';

import { isMobileView, isValidDate } from '@core/utils';
import { CalendarComponent } from '@web/_shared/components/calendar/calendar.component';
import { DatePickerComponent } from '@mobile/_shared/sheets/date-picker/date-picker.component';
import { IconComponent } from '@shared/components/ui/icon/icon.component';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';

@UntilDestroy()
@Component({
  selector: 'app-date-picker-input',
  imports: [CommonModule, FormsModule, IconComponent, ReactiveFormsModule, TranslateModule, TippyModule, CalendarComponent],
  template: `
    <div class="input-icon-postfix">
      <input
        #dateInput="tippy"
        [tippy]="calendar"
        (click)="allowManualInput ? $event.preventDefault() : openDatePicker()"
        (input)="onInput($event)"
        [value]="value | date: 'dd.MM.yyyy'"
        [id]="id"
        [name]="name"
        [placeholder]="allowManualInput ? 'DD.MM.YYYY' : (placeholder | translate)"
        type="text"
        [disabled]="disabled"
        [required]="required"
        [readOnly]="allowManualInput ? false : readOnly"
        className="popper-menu z-9999"
        placement="bottom"
        trigger="manual"
        variation="popper"
      />
      <app-icon cssClass="cursor-pointer" (click)="openDatePicker()" iconName="calendar-mini"></app-icon>
    </div>
    <ng-template #calendar let-hide>
      <app-calendar [startDateSelectionFrom]="startDateSelectionFrom" [selectedDayInput]="value" [pastDatesSelection]="pastDatesSelection" (dateSelected)="dateSelection($event)"></app-calendar>
    </ng-template>
  `,
  styles: [
    `
      :host.ng-invalid.ng-dirty,
      :host.ng-invalid.ng-touched {
        input {
          border: 1px solid var(--red);
        }
      }

      .input-icon-postfix {
        input {
          padding-left: 16px;
        }
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerInputComponent),
      multi: true,
    },
  ],
})
export class DatePickerInputComponent implements ControlValueAccessor {
  @Input() value = '';
  @Input() placeholder = 'Select Date';
  @Input() disabled: boolean;
  @Input() required: boolean;
  @Input() name = 'date-selector';
  @Input() id = 'date-selector';
  @Input() readOnly = true;
  @Input() pastDatesSelection = true;
  @Input() preSelectedDate: string;
  @Input() allowManualInput = false;
  @ViewChild('dateInput', { static: true }) dateInput: TippyInstance;
  private readonly _bottomSheetService = inject(NgxBottomSheetService);
  private _onChange: (value: any) => void;
  private _onTouched: () => void;

  get startDateSelectionFrom() {
    if (!this.preSelectedDate) return null;
    if (isValidDate(this.preSelectedDate)) {
      return this.preSelectedDate;
    }
    return null;
  }

  writeValue(value: string): void {
    if (!value) return;
    if (isValidDate(value)) {
      this.value = value;
    } else {
      this.value = '';
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  openDatePicker() {
    if (this._onChange) {
      this._onChange(this.value);
    }

    if (!isMobileView()) {
      this.dateInput.show();
      return;
    }
    const selectedDate = this._bottomSheetService.open(DatePickerComponent, {
      height: '95%',
      showCloseButton: false,
      data: {
        currentSelectedDate: this.value,
        maxHeight: '95%',
        fixedHeaderHeight: 60,
        pastDatesSelection: this.pastDatesSelection,
        startDateSelectionFrom: this.startDateSelectionFrom,
      },
    });
    selectedDate.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (date) => {
        if (date) {
          this.value = date;
          if (this._onChange) {
            this._onChange(date);
          }
        }
      },
    });
  }

  dateSelection(date: string) {
    this.value = date;
    this.dateInput.hide();
    if (this._onChange) {
      this._onChange(date);
    }
  }

  onInput(event: any): void {
    if (!this.allowManualInput) {
      return;
    }

    const inputValue = event.target.value.replace(/\D/g, ''); // strict to digits only
    let formattedValue = '';

    const currentYear = new Date().getFullYear();
    const maxYear = currentYear + 10;

    if (inputValue.length > 0) {
      const day = parseInt(inputValue.slice(0, 2), 10);
      formattedValue += day > 31 ? '31' : inputValue.slice(0, 2); // Restrict days to 31
    }
    if (inputValue.length > 2) {
      const month = parseInt(inputValue.slice(2, 4), 10);
      formattedValue += '.' + (month > 12 ? '12' : inputValue.slice(2, 4)); // Restrict months to 12
    }
    if (inputValue.length > 4) {
      const year = parseInt(inputValue.slice(4, 8), 10);
      formattedValue += '.' + (year > maxYear ? maxYear : inputValue.slice(4, 8)); // Restrict year to maxYear
    }

    event.target.value = formattedValue;

    if (formattedValue.length === 10 && this._isValidDate(formattedValue)) {
      const [day, month, year] = formattedValue.split('.').map(Number);
      const date = new Date(year, month - 1, day, 12, 0, 0, 0);
      const isoDate = date.toISOString().split('T')[0];
      if (this.value !== isoDate) {
        this.value = isoDate;
        if (this._onChange) {
          this._onChange(this.value);
        }
      }
    } else if (inputValue.length === 0) {
      this.value = '';
      if (this._onChange) {
        this._onChange(this.value);
      }
    }
  }

  private _isValidDate(dateString: string): boolean {
    const [day, month, year] = dateString.split('.').map(Number);
    const date = new Date(year, month - 1, day);
    return !isNaN(date.getTime()) && date.getDate() === day && date.getMonth() + 1 === month && date.getFullYear() === year;
  }
}
